/**
 * Ordering catalogue rows against what somebody typed.
 *
 * The catalogue holds 192 rows whose name contains the word "pomidorai" and
 * exactly one row whose name *is* "Pomidorai". Every ranking the app had until
 * now scored all 192 identically — `word_similarity` returns 1.0 the moment the
 * query matches any run of words in the name, so a plain tomato and "Saulėje
 * džiovinti pomidorai RISTORIS, aliejuje, 800g/400g" came out level and the
 * order among them was whatever Postgres happened to return. That is why the
 * obvious answer sat twentieth.
 *
 * So the measure has to be two-sided. Matching every word of the query is not
 * enough on its own — it only says the row *answers* the question. What
 * separates the tomato from the sun-dried tomatoes in oil is the other
 * direction: how much of the row's own name the query failed to account for.
 * Every unexplained word is a narrowing nobody asked for.
 *
 *     relevance = queryCoverage × (FLOOR + (1 − FLOOR) × nameCoverage)
 *
 * Both factors are 1.0 only when the query and the name are about the same
 * thing and nothing else, so the food that is simply what you typed lands a
 * clear third above the products made from it, rather than tying with them.
 *
 * Matching individual words is Lithuanian-aware because the query and the shelf
 * never share a spelling: somebody types "varškės" and the pot says "Varškė".
 * `stemLt` collapses the two, prefixes carry a half-typed word, and a bounded
 * edit distance covers a thumb landing on the wrong key. What this deliberately
 * does *not* do is derivation — "aguonos" and "aguoninis" stay apart, because a
 * search that quietly widens is worse than one that finds nothing and says so.
 *
 * The same scorer runs on the server and on the phone. That is the point: the
 * phone holds a few thousand cached rows and the server holds twelve thousand,
 * and the results used to be concatenated — everything local first, in
 * alphabetical order, then everything remote — which buried the server's best
 * answer under whatever the phone had eaten before. One scorer means one list.
 */

import { foldLt, stemLt } from './lithuanian.js'

export interface SearchableFood {
  nameLt?: string | null
  nameEn?: string | null
  brand?: string | null
  category?: string | null
  isVerified?: boolean
}

/** What the diary knows about a food, which is the strongest hint there is. */
export interface FoodUsage {
  /** Diary entries naming this food. */
  timesLogged?: number
  /** When the diary last named it, ISO-8601. Absent where nobody knows. */
  lastUsedAt?: string | null
  favourite?: boolean
}

export interface RankOptions {
  /** Usage by food id. Absent where the caller has no diary to hand. */
  usage?: ReadonlyMap<string, FoodUsage>
  /** The clock, so recency is a value a test can pin rather than the wall. */
  now?: number
  /**
   * Rank the plain ingredient above the products made from it.
   *
   * A typed search is a question about the shop shelf, where "bananas" quite
   * reasonably returns a banana milkshake. A photograph is not: the picture
   * showed a banana, and offering the milkshake first is the search box
   * answering a question nobody asked.
   */
  preferGeneric?: boolean
  /** Rows scoring below this are dropped rather than shown. */
  floor?: number
}

// ─── Weights ─────────────────────────────────────────────────────────────────

/**
 * How much of the score survives when the name says things the query did not.
 *
 * At 0.55 a row whose name is exactly the query scores 1.00 and a row carrying
 * three extra words scores 0.66 — a gap no tie-breaker below can close, which
 * is the whole point: nothing about a product being familiar should put
 * "Pomidorų kečupas" above "Pomidorai" for somebody who typed "pomidorai".
 */
const SPECIFICITY_FLOOR = 0.55

/** The name *is* the query. Nothing outranks that. */
const EXACT_BONUS = 0.25

/** The name begins with the query: "Varškė 9%" for "varškė". */
const PREFIX_BONUS = 0.1

/**
 * Foods the diary has seen before, on a curve that flattens fast.
 *
 * A food logged once is barely a signal and a food logged forty times is not
 * four times the signal of one logged ten — it is the same answer, held more
 * firmly. So the first entry is worth most of the boost and the rest is a slow
 * climb, capped where it can still lose to an exact name.
 */
const USAGE_BASE = 0.15
const USAGE_SPAN = 0.25
const USAGE_SATURATION = 10
const FAVOURITE_BONUS = 0.06

/**
 * How much of that familiarity survives not having eaten the thing lately.
 *
 * What somebody ate this week is a better guess at what they are typing now
 * than what they ate last spring, and a search box that has watched a diary for
 * a year knows a great many foods equally well. So recency is spent out of the
 * familiarity budget rather than added on top of it: the ceiling above does not
 * move, six hundredths separate a food eaten today from the same food eaten
 * last spring, and that is enough to reorder near-equals and nothing else.
 *
 * A week, because a shopping trip is a week. Beyond a month the curve has
 * essentially stopped moving, which is right — the difference between forgotten
 * in March and forgotten in April is not information.
 */
const RECENCY_KEEP = 0.85
const RECENCY_HALF_LIFE_DAYS = 7

const DAY_MS = 86_400_000

/**
 * 1 for a food eaten today, falling to RECENCY_KEEP for one nobody has touched
 * in months.
 *
 * An unknown timestamp returns 1 rather than a penalty: not knowing when
 * something was last eaten is not evidence that it was long ago, and every
 * caller that has yet to be taught to pass one must keep scoring exactly as it
 * does today. A timestamp in the future returns 1 too — a phone with a wrong
 * clock should not be able to invent a bonus — and `Number.isFinite` is
 * load-bearing rather than defensive, because `Date.parse` answers NaN instead
 * of throwing and one NaN score reshuffles the entire sort silently.
 */
function freshness(lastUsedAt: string | null | undefined, now: number): number {
  if (!lastUsedAt) return 1
  const days = (now - Date.parse(lastUsedAt)) / DAY_MS
  if (!Number.isFinite(days) || days <= 0) return 1
  return RECENCY_KEEP + (1 - RECENCY_KEEP) * 0.5 ** (days / RECENCY_HALF_LIFE_DAYS)
}

/** Lab-measured and unbranded, which is what the seeded half of the shelf is. */
const VERIFIED_BONUS = 0.05
const GENERIC_BONUS = 0.15

/** Half the query unanswered is not a result, it is noise with a score. */
export const MIN_COVERAGE = 0.4

/**
 * How far below the best answer's coverage a row may still be shown.
 *
 * A row that answered one of two typed words is a fallback, not a result:
 * where the catalogue holds an actual "Dvaro varškė" nobody wants the hundred
 * other curds and the hundred other Dvaro products listed underneath it. But
 * where it holds nothing matching both, those near misses are the only useful
 * thing on the screen — far better than "nieko nerasta" from a catalogue that
 * plainly has curd in it. So the cut is relative to the best row found rather
 * than absolute: a quarter of the query may go unanswered, and only when
 * something else answered more.
 */
const COVERAGE_BAND = 0.25

// ─── Words ───────────────────────────────────────────────────────────────────

/**
 * Words that describe the packet rather than the food. Digits are already gone
 * — the split keeps letters only — so this is just what is left of "400/240 g"
 * and "2,5kg" once the numbers are dropped.
 */
const NOISE = new Set([
  'kg',
  'ml',
  'vnt',
  'apie',
  'su',
  'be',
  'is',
  'ir',
  'ant',
  'po',
  'prie',
  'arba',
  'bei',
  'iki',
  'nuo',
  'the',
  'and',
  'with',
])

export interface SearchWord {
  word: string
  stem: string
}

/** Folded, split on anything that is not a letter, stripped of packet words. */
export function searchWords(text: string | null | undefined): SearchWord[] {
  if (!text) return []
  return foldLt(text)
    .split(/[^a-z]+/)
    .filter((word) => word.length >= 2 && !NOISE.has(word))
    .map((word) => ({ word, stem: stemLt(word) }))
}

/**
 * The substring a database has to hold for a row to be worth scoring — the
 * stem, one letter shorter once it can spare one.
 *
 * Lithuanian softens the last consonant of a stem before some endings, and the
 * stemmer is too shallow to undo it: "gražgarstė" becomes "gražgarsčių" in the
 * genitive plural, so `grazgarst` is not a substring of `grazgarsciu` and a
 * search for watercress finds none of the watercress in the catalogue. Dropping
 * the final letter costs almost nothing in selectivity at eight characters and
 * covers every t→č and d→dž the language does. Short stems keep every letter,
 * where one is a much larger share of what they identify.
 *
 * Only ever used to widen what gets looked at. The ranking still compares whole
 * stems, so the softened spelling scores as the near-miss it is rather than as
 * an exact hit.
 */
export function recallStem(stem: string): string {
  return stem.length >= 7 ? stem.slice(0, -1) : stem
}

/** Every substring a row must contain to be a candidate for this query. */
export function recallStems(query: string): string[] {
  return prepareQuery(query).terms.map((term) => recallStem(term.stem))
}

/**
 * Edits between two words, given up on once it passes `max`.
 *
 * Deliberately not a trigram or bigram overlap. Those measure how much two
 * words look alike, which is a different question from whether one is the other
 * mistyped, and they get it wrong in exactly the way this catalogue punishes:
 * "margarin" and "marinar" share five of their six bigrams and are a block of
 * margarine and a pasta sauce. Four edits apart is the honest answer, and one
 * edit is what a thumb on a phone keyboard actually produces.
 */
function editsWithin(a: string, b: string, max: number): number {
  if (Math.abs(a.length - b.length) > max) return max + 1
  let previous = Array.from({ length: b.length + 1 }, (_, i) => i)
  for (let i = 1; i <= a.length; i++) {
    const current = [i]
    let best = i
    for (let j = 1; j <= b.length; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1
      const value = Math.min(current[j - 1]! + 1, previous[j]! + 1, previous[j - 1]! + cost)
      current.push(value)
      if (value < best) best = value
    }
    if (best > max) return max + 1
    previous = current
  }
  return previous[b.length]!
}

/**
 * How wrong a word may be and still be the same word.
 *
 * Nothing for a short stem — at four letters one edit is a different food — and
 * never more than two, because past that the measure stops being about typing.
 */
function typoBudget(length: number): number {
  if (length >= 7) return 2
  if (length >= 5) return 1
  return 0
}

/**
 * How well one typed word answers one catalogue word, 0 to 1.
 *
 * The ladder is deliberate: the same word beats the same stem beats a prefix of
 * it beats something that merely looks like it. Each rung is a weaker claim
 * about meaning, and keeping them apart is what stops a search from widening
 * until it finds nothing in particular.
 */
export function wordMatchStrength(query: SearchWord, name: SearchWord): number {
  if (query.word === name.word) return 1
  if (query.stem === name.stem) return 0.92
  // Half-typed, or an ending the shallow stemmer left on: "pomid" for
  // "pomidorai", "krutinelems" for "krūtinėlė".
  if (name.stem.startsWith(query.stem) || query.stem.startsWith(name.stem)) return 0.75
  const budget = typoBudget(Math.min(query.stem.length, name.stem.length))
  if (budget === 0) return 0
  const edits = editsWithin(query.stem, name.stem, budget)
  return edits > budget ? 0 : 0.75 - 0.15 * edits
}

// ─── Scoring ─────────────────────────────────────────────────────────────────

export interface PreparedQuery {
  /** Folded and collapsed, for comparing against a whole name. */
  normalized: string
  terms: SearchWord[]
}

export function prepareQuery(query: string): PreparedQuery {
  return { normalized: foldLt(query).replace(/\s+/g, ' ').trim(), terms: searchWords(query) }
}

interface NameScore {
  score: number
  /** How much of what was typed this row actually answered, 0 to 1. */
  coverage: number
}

export interface FoodScore extends NameScore {
  /** The name on screen is the query, character for character. */
  exact: boolean
}

const NO_NAME: NameScore = { score: 0, coverage: 0 }
const NO_MATCH: FoodScore = { score: 0, coverage: 0, exact: false }

/**
 * One name, scored on its own. A row carries up to two, and `scoreFood` decides
 * how much the one nobody sees is allowed to count for.
 */
function scoreName(
  query: PreparedQuery,
  name: string | null | undefined,
  brandWords: SearchWord[],
): NameScore {
  const words = searchWords(name)
  if (words.length === 0 || query.terms.length === 0) return NO_NAME

  const matched = new Set<number>()
  let answered = 0
  for (const term of query.terms) {
    let best = 0
    let bestIndex = -1
    for (const [index, word] of words.entries()) {
      const strength = wordMatchStrength(term, word)
      if (strength > best) {
        best = strength
        bestIndex = index
      }
    }
    // A brand answers the query without being something the name left
    // unexplained: somebody typing "rimi pomidorai" named the shop on purpose.
    for (const word of brandWords) best = Math.max(best, wordMatchStrength(term, word))
    if (bestIndex >= 0 && best > 0) matched.add(bestIndex)
    answered += best
  }

  const coverage = answered / query.terms.length
  if (coverage < MIN_COVERAGE) return NO_NAME
  const nameCoverage = matched.size / words.length
  return {
    score: coverage * (SPECIFICITY_FLOOR + (1 - SPECIFICITY_FLOOR) * nameCoverage),
    coverage,
  }
}

/**
 * What the row is called on screen — and the only name allowed to claim that
 * the row *is* the query.
 *
 * Open Food Facts rows routinely carry a translation field somebody filled in
 * with the category instead: an "Amfora varškė su šilauogėmis" whose English
 * name is the single word "VARŠKĖ". Scoring both names and taking the better
 * handed that blueberry dessert the same perfect mark as a plain pot of curd,
 * for a name nobody would ever see on the row.
 */
function displayName(food: SearchableFood): string {
  return food.nameLt ?? food.nameEn ?? ''
}

/** How much of a match on the other-language name counts. */
const SECONDARY_NAME_FACTOR = 0.85

/**
 * How well a catalogue row answers a typed query. Score runs roughly 0 to 1.8;
 * only the order matters, and the caller may show it as a confidence.
 */
export function scoreFood(
  query: PreparedQuery,
  food: SearchableFood,
  usage?: FoodUsage,
  options: Pick<RankOptions, 'preferGeneric' | 'now'> = {},
): FoodScore {
  const brandWords = searchWords(food.brand)
  const shown = displayName(food)
  const lt = scoreName(query, food.nameLt, brandWords)
  const en = scoreName(query, food.nameEn, brandWords)
  // The name on screen counts in full; the other one is evidence at a discount,
  // and only where the row actually has both. A row named only in English is
  // named in English — nothing is being demoted there.
  const primary = food.nameLt == null ? en : lt
  const secondary = food.nameLt == null ? lt : en
  const discounted = { ...secondary, score: secondary.score * SECONDARY_NAME_FACTOR }
  const best = discounted.score > primary.score ? discounted : primary
  if (best.score === 0) return NO_MATCH

  let score = best.score
  const folded = foldLt(shown).replace(/\s+/g, ' ').trim()
  const exact = folded === query.normalized
  if (exact) score += EXACT_BONUS
  else if (folded.startsWith(`${query.normalized} `)) score += PREFIX_BONUS
  const logged = usage?.timesLogged ?? 0
  if (logged > 0) {
    const reach = Math.log1p(Math.min(logged, USAGE_SATURATION)) / Math.log1p(USAGE_SATURATION)
    score +=
      (USAGE_BASE + USAGE_SPAN * reach) * freshness(usage?.lastUsedAt, options.now ?? Date.now())
  }
  if (usage?.favourite) score += FAVOURITE_BONUS
  if (food.isVerified) score += VERIFIED_BONUS
  if (options.preferGeneric && !food.brand && food.category) score += GENERIC_BONUS
  return { score, coverage: best.coverage, exact }
}

export interface ScoredFood<T> extends FoodScore {
  food: T
}

/**
 * The whole result list, best first.
 *
 * Ties break on the shorter name and then alphabetically rather than on
 * whatever order the rows arrived in, so the same query always produces the
 * same list — a search that reshuffles itself between keystrokes reads as
 * broken even when every row in it is right.
 */
export function rankFoods<T extends SearchableFood & { id: string }>(
  query: string,
  foods: readonly T[],
  options: RankOptions = {},
): Array<ScoredFood<T>> {
  const prepared = prepareQuery(query)
  if (prepared.terms.length === 0) return []
  const floor = options.floor ?? 0
  const now = options.now ?? Date.now()

  let scored: Array<ScoredFood<T>> = []
  let bestCoverage = 0
  for (const food of foods) {
    const scoredFood = scoreFood(prepared, food, options.usage?.get(food.id), { ...options, now })
    if (scoredFood.score > floor) {
      scored.push({ food, ...scoredFood })
      if (scoredFood.coverage > bestCoverage) bestCoverage = scoredFood.coverage
    }
  }
  const wanted = bestCoverage - COVERAGE_BAND
  if (wanted > MIN_COVERAGE) scored = scored.filter((match) => match.coverage >= wanted)

  return scored.sort((a, b) => {
    // The row whose name IS the query goes first, whatever else is true of it.
    // Everything below is a bonus and bonuses add up: forty entries of
    // "Pomidorai VICTORIA" plus a star already came to more than the exact
    // bonus, which put a branded tomato above the tomato for somebody who had
    // typed the tomato's name. A tier cannot be out-added.
    if (a.exact !== b.exact) return a.exact ? -1 : 1
    if (b.score !== a.score) return b.score - a.score
    const an = displayName(a.food).length
    const bn = displayName(b.food).length
    if (an !== bn) return an - bn
    return displayName(a.food).localeCompare(displayName(b.food), 'lt')
  })
}

/**
 * Collapse rows that are the same product entered twice.
 *
 * The catalogue has fifty-odd of these — the same yoghurt scraped from two
 * pages, the same curd pushed by two phones — and three identical lines in a
 * result list read as a broken search rather than as a thorough one. Identity
 * is the folded name, the brand and the energy together: two rows that agree on
 * all three are the same product however they got there, and two that differ on
 * energy are genuinely different and both worth showing.
 */
export function dedupeFoods<T extends SearchableFood & { id: string; kcal: number }>(
  foods: readonly T[],
): T[] {
  const seen = new Map<string, T>()
  for (const food of foods) {
    const key = [
      foldLt(food.nameLt ?? food.nameEn ?? '').trim(),
      foldLt(food.brand ?? '').trim(),
      Math.round(food.kcal),
    ].join('|')
    const kept = seen.get(key)
    // Keep the verified copy where there is one; otherwise the first, which is
    // the better-ranked one because the list is already sorted.
    if (kept == null || (!kept.isVerified && food.isVerified)) seen.set(key, food)
  }
  return [...seen.values()]
}
